import { Injectable, Injector } from '@angular/core';
import { Observable, of, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoggerService } from '../../../services/logger/logger.service';
import { SalePriceSchedule } from '../models/sale-price-schedule';
import { SalePriceScheduleService } from './sale-price-schedule.service';
import { BookingSlot } from '../models/booking-slot';
import { SalePriceScheduleType } from '../models/sale-price-schedule-type';
import { GlobalSettings } from 'src/environments/global-settings';


@Injectable()
export class SalePriceScheduleStateService {
  private _salePriceSchedules = new Array<SalePriceSchedule>();
  private _salePriceScheduleTypes = new Array<SalePriceScheduleType>();
  private _salePriceScheduleTypesDict = {};

  constructor(
    private logger: LoggerService,
    private salePriceScheduleService: SalePriceScheduleService,
  ) {
  }

  initialize(): Observable<boolean> {
    return Observable.create(
      (observable: Subscriber<boolean>) => {
        this.logger.info(`SalePriceScheduleStateService: initialize.`);
        this.reset();
        this.loadAllSalePriceSchedule().subscribe(
          done => {
            this.loadScheduleTypes().subscribe(
              result => {
                this.logger.info(`SalePriceScheduleStateService: loadScheduleTypes done.`);
              },
              error => {
                this.logger.info(`SalePriceScheduleStateService: loadScheduleTypes initialize failed ${error}.`);
              }
            );
            this.logger.info(`SalePriceScheduleStateService: loadAllSalePriceSchedule done.`);
            observable.next(done);
          },
          error => {
            this.logger.info(`SalePriceScheduleStateService: loadAllSalePriceSchedule initialize failed ${error}.`);
            observable.error(error);
          },
          () => {
            // observable.complete();
          }
        );
      });
  }

  get salePriceSchedules(): SalePriceSchedule[] {
    return this._salePriceSchedules;
  }

  get salePriceScheduleTypes(): SalePriceScheduleType[] {
    return this._salePriceScheduleTypes;
  }

  reset() {
    this._salePriceSchedules = new Array<SalePriceSchedule>();
    this._salePriceScheduleTypes = new Array<SalePriceScheduleType>();
  }

  loadAllSalePriceSchedule(): Observable<boolean> {
    return this.salePriceScheduleService.getAllSalePriceSchedule()
      .pipe(map(
        (result: SalePriceSchedule[]) => {
          if (result) {
            this._salePriceSchedules = result;
            return true;
          }
          return false;
        }
      ));
  }

  loadScheduleTypes(): Observable<boolean> {
    return this.salePriceScheduleService.getAllScheduleTypes()
      .pipe(map(
        (result: SalePriceScheduleType[]) => {
          if (result) {
            this._salePriceScheduleTypes = result;
            for (const res of result) {
              this._salePriceScheduleTypesDict[res.id] = res;
            }
            return true;
          }
          return false;
        }
      ));
  }

  getSalePriceScheduleTypeById(id: number): SalePriceScheduleType {
    return this._salePriceScheduleTypesDict[id];
  }

  getSalePriceScheduleTypeByIds(ids: number[]): SalePriceScheduleType[] {
    const salePriceScheduleTypeByIds = [];
    for (const id of ids) {
      const salePriceScheduleType = this.getSalePriceScheduleTypeById(id);
      if (salePriceScheduleType) {
        salePriceScheduleTypeByIds.push(salePriceScheduleType);
      }
    }
    return salePriceScheduleTypeByIds;
  }

  getSortedSalePriceScheduleTypeByIds(ids: number[]): SalePriceScheduleType[] {
    if (!ids) {
      return [];
    }
    const salePriceScheduleTypes = this.getSalePriceScheduleTypeByIds(ids);

    if (salePriceScheduleTypes && salePriceScheduleTypes.length > 0) {
      salePriceScheduleTypes.sort(function (a, b) {
        if (a.sequence < b.sequence) { return -1; }
        if (a.sequence > b.sequence) { return 1; }
        return 0;
      });
    }

    return salePriceScheduleTypes;
  }

  getActualSalePriceScheduleTypeIds(bookingSlots: BookingSlot[]) {
    const actualSalePriceScheduleTypeIds = [];
    if (!this.salePriceSchedules || !this.salePriceScheduleTypes) {
      return actualSalePriceScheduleTypeIds;
    }

    let bookingDate = GlobalSettings.DateNow;

    if (bookingSlots && bookingSlots.length > 0) {
      bookingDate = bookingSlots[0].bookingDate;
    }

    bookingDate = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());

    const dayofWeek = bookingDate.getDay() - 1 >= 0 ? bookingDate.getDay() - 1 : 6;

    for (const salePriceSchedule of this.salePriceSchedules) {
      let startDate = new Date(salePriceSchedule.startDate);
      startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      let endDate = null;
      if (salePriceSchedule.endDate) {
        endDate = new Date(salePriceSchedule.endDate);
        endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      }

      if (startDate <= bookingDate && (!endDate || bookingDate <= endDate)) {
        let isScheduleValid = true;
        if (bookingSlots) {
          isScheduleValid = this.validateScheduleBySlot(salePriceSchedule, bookingSlots[0], dayofWeek);
          if (isScheduleValid
            && salePriceSchedule.salePriceScheduleType
            && salePriceSchedule.salePriceScheduleType.id
            && actualSalePriceScheduleTypeIds.indexOf(salePriceSchedule.salePriceScheduleType.id) === -1) {
            actualSalePriceScheduleTypeIds.push(salePriceSchedule.salePriceScheduleType.id);
          }
        }
      }
    }

    const sortedActualSalePriceScheduleTypeIds = [];
    const salePriceScheduleTypes = this.getSortedSalePriceScheduleTypeByIds(actualSalePriceScheduleTypeIds);
    for (const actualSalePriceScheduleType of salePriceScheduleTypes) {
      sortedActualSalePriceScheduleTypeIds.push(actualSalePriceScheduleType.id);
    }
    return sortedActualSalePriceScheduleTypeIds;
  }

  validateScheduleBySlot(salePriceSchedule: SalePriceSchedule, bookingSlot: BookingSlot, dayofWeek: number): boolean {
    let isValid = false;
    if (!bookingSlot) {
      return isValid;
    }

    if (salePriceSchedule && salePriceSchedule.salePriceScheduleSlots && salePriceSchedule.salePriceScheduleSlots.length > 0) {
      for (const scheduleSlot of salePriceSchedule.salePriceScheduleSlots) {
        if (scheduleSlot.dayOfWeek === dayofWeek
          && bookingSlot.fromTime >= scheduleSlot.fromTime
          && bookingSlot.toTime <= scheduleSlot.toTime) {
          isValid = true;
          break;
        }
      }
    }

    return isValid;
  }

}
